import { CustomerOrderBase } from '../../../../contracts/customer-order.js';
import { fromEnumAsString, toEnumFromString } from '../../../../serialization/enum-utils.js';
import { MapUtils } from '../../../../utility/map-utils.js';
import { VersionRange } from '../../../core/version-range.js';
import { cloneDataOrNull, DataModel, DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData } from '../../../dto/dto.js';
import { BusinessModuleRef } from '../../business-module-ref.js';
import { Country } from '../../country.js';
import { OpenAccessPassSource } from '../../open-access-pass/open-access-pass.js';
import { OrderDomain } from '../../order-domain.js';
import { PaymentMethod } from '../../payment-method.js';
import { getCountryOfTimeZone, TimeZoneRegion } from '../../time-zone-region.js';

import { LegacyPassCartItemDto } from './legacy-pass-cart-item.js';

export class LegacyPassCartDto extends DataModel {
  static readonly TYPE_ID: string = 'f71aa7d417744bfeb292e03fc558dda1';
  static readonly SCHEMA_VERSION: number = 4;

  private constructor(
      public readonly businessRef: BusinessModuleRef,
      public readonly items: LegacyPassCartItemDto[],
      public readonly creatorId: string,
      public readonly viaOrder: CustomerOrderBase | null,
      public readonly paymentMethod: PaymentMethod,
      public readonly source: OpenAccessPassSource,
      public readonly country: Country,
      public readonly timezone: TimeZoneRegion,
      public readonly domain: OrderDomain,
  ) {
    super();
  }

  get $typeId() {
    return LegacyPassCartDto.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    // Version should always be `exactly`.
    // Cart should never be backwards migrated, backend should be updated before frontend.
    return VersionRange.exactly(LegacyPassCartDto.SCHEMA_VERSION);
  }

  static create(args: {
    businessRef: BusinessModuleRef,
    items: LegacyPassCartItemDto[],
    creatorId: string,
    viaOrder: CustomerOrderBase | null,
    paymentMethod: PaymentMethod,
    source: OpenAccessPassSource,
    country: Country,
    timezone: TimeZoneRegion,
    domain: OrderDomain,
  }): LegacyPassCartDto {
    return new LegacyPassCartDto(
        args.businessRef,
        args.items,
        args.creatorId,
        args.viaOrder,
        args.paymentMethod,
        args.source,
        args.country,
        args.timezone,
        args.domain,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyPassCartDto {
    return new LegacyPassCartDto(
        BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
        MapUtils.getList<LegacyPassCartItemDto>(map, 'items', (e) => {
          return context.fromData(e as SerializedData);
        }),
        MapUtils.getString(map, 'creatorId'),
        context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'viaOrder')),
        toEnumFromString(MapUtils.getString(map, 'paymentMethod'), PaymentMethod),
        toEnumFromString(MapUtils.get<string>(map, 'source'), OpenAccessPassSource),
        toEnumFromString(MapUtils.get<string>(map, 'country'), Country),
        toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion),
        toEnumFromString(MapUtils.get<string>(map, 'domain'), OrderDomain),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'items': this.items.map((e) => context.toData(e)),
      'creatorId': this.creatorId,
      'viaOrder': context.toDataOrNull(this.viaOrder),
      'paymentMethod': fromEnumAsString(this.paymentMethod),
      'source': fromEnumAsString(this.source),
      'country': fromEnumAsString(this.country),
      'timezone': fromEnumAsString(this.timezone),
      'domain': fromEnumAsString(this.domain),
    };
  }

  clone(): this {
    return new LegacyPassCartDto(
        this.businessRef.clone(),
        this.items.map((e) => e.clone()),
        this.creatorId,
        cloneDataOrNull(this.viaOrder),
        this.paymentMethod,
        this.source,
        this.country,
        this.timezone,
        this.domain,
    ) as this;
  }

  isSame(other: LegacyPassCartDto): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      isSameData(other.viaOrder, this.viaOrder) &&
      isSameList(other.items, this.items, isSameData) &&
      other.source === this.source &&
      other.country === this.country &&
      other.timezone === this.timezone &&
      other.domain === this.domain &&
      other.creatorId === this.creatorId &&
      other.paymentMethod === this.paymentMethod;
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof LegacyPassCartDto;
  }

  static assertType(order: DataModel): order is LegacyPassCartDto {
    return order instanceof LegacyPassCartDto;
  }
}

export class LegacyPassCartDtoBuilder extends DataModelBuilder<LegacyPassCartDto> {
  build(context: SerializationContext, map: SerializedData): LegacyPassCartDto {
    return LegacyPassCartDto.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(LegacyPassCartDto.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LegacyPassCartDto.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version:
    // * [viaOrder] field of type [CustomerOrderBase | null] was introduced
    // * [source] field of type [OpenAccessPassSource] was introduced
    // * [timezone] field of type [TimeZoneRegion] was introduced
    if(version < 2) {
      map['source'] = fromEnumAsString(OpenAccessPassSource.Admin);
      map['timezone'] = fromEnumAsString(TimeZoneRegion.EuropeBucharest);
      version = 2;
    }

    // In this version parameter `country` of type `Country` was introduced.
    if(version < 3) {
      const timezone = toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion);
      map['country'] = fromEnumAsString(getCountryOfTimeZone(timezone) ?? Country.Romania);
      version = 3;
    }

    // In this version parameter `domain` of type `OrderDomain` was introduced.
    if(version < 4) {
      map['domain'] = fromEnumAsString(OrderDomain.inTime);
      version = 4;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
